import React from 'react';
import { IStorefrontBanner, IStorefrontPage } from '../../../../models/Storefront';
import StorefrontBannerEditorPanel from './StorefrontBannerEditorPanel';
import { useLocation } from 'react-router-dom';
import { useUrlPanelState } from '../../../../hooks/useUrlState';
import { bannersForPage, IHeroBannerOptions } from '../pagesConfig';
import { bannerRotationMs } from '../homePageConfig';
import { elementStyleFrom, textStyleFrom } from '../styleControls';
import {
    hasBannerOverlayContent,
    overlayBoxStyleFrom,
    overlayContentStyleFrom,
    overlayFrameStyleFrom,
    overlayFramedLayerStyle,
    overlayLayerStyleFrom,
    overlayScaledFrameStyle,
    parseBannerOverlayConfig,
} from '../bannerOverlayConfig';

// (The local OVERLAY_JUSTIFY / OVERLAY_ALIGN maps that used to live here are gone: the alignment
// logic now comes from the shared overlay builders in bannerOverlayConfig, ported from the runtime,
// so the two sides can't drift on what "bottom-left" means.)

// The overlay text/button stack rendered over a banner slide. Mirrors the shopper-side
// rendering; in the editor preview the button must not navigate. When `contentMaxWidth` is
// set (full-width banner) a centered page-frame is inserted between the full-bleed absolute
// layer and the content column so the overlay text stays locked to the page maxWidth.
export const BannerOverlay: React.FC<{
    banner: IStorefrontBanner;
    contentMaxWidth?: number;
    /** homePageConfig.bannerOverlayScale — size the overlay in cqw so it tracks the banner width. */
    overlayScale?: boolean;
    // (No `editable` any more: the CTA used to cancel its own navigation, but only in edit mode.
    // PreviewCanvas now cancels navigation for every anchor in the preview, in both modes.)
}> = ({ banner, contentMaxWidth, overlayScale }) => {
    const overlay = parseBannerOverlayConfig(banner.overlayConfig);
    if (!hasBannerOverlayContent(overlay)) {
        return null;
    }

    // Same three-way choice the live page makes (HomePage.tsx `overlayNode`):
    //  - full-bleed + overlay-scale : bare layer, cqw-sized frame (position AND size track the banner)
    //  - full-bleed                 : bare layer, page-width frame
    //  - otherwise                  : one positioned layer filling the banner
    // `contentMaxWidth` is only ever set in full-width mode, so it doubles as the full-bleed test —
    // exactly as it did before this change.
    const content = (
        <div
            style={{
                ...overlayContentStyleFrom(overlay.position),
                // Backdrop box (background/opacity, border, radius, padding) around the stack.
                ...overlayBoxStyleFrom(overlay),
            }}
        >
                {overlay.title?.trim() && (
                    <div style={{ fontSize: '2.5rem', fontWeight: 600, lineHeight: 1.2, color: '#1f2933', ...textStyleFrom(overlay.titleStyle) }}>
                        {overlay.title}
                    </div>
                )}
                {overlay.subtitle?.trim() && (
                    // lineHeight: the storefront renders this as a <p>, which browsers give
                    // `line-height: normal`. A <div> inside the editor instead INHERITS the admin
                    // canvas's 20px, so a large subtitle font would sit in a 20px-tall box. See the
                    // CTA below for the same fix and why it matters.
                    <div style={{ fontSize: '1.1rem', lineHeight: 'normal', color: '#1f2933', ...textStyleFrom(overlay.subtitleStyle) }}>
                        {overlay.subtitle}
                    </div>
                )}
                {overlay.button?.label?.trim() && (
                    <a
                        href={overlay.button.url || banner.linkUrl || '#'}
                        style={{
                            pointerEvents: 'auto',
                            display: 'inline-block',
                            backgroundColor: '#0f6cbd',
                            color: '#fff',
                            padding: '12px 28px',
                            borderRadius: 4,
                            textDecoration: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            // The storefront's CTA is a <button>, which does NOT inherit line-height —
                            // it computes `normal`, so the text box grows with the font. This <a> does
                            // inherit, picking up the admin canvas's 20px, which held the button to
                            // roughly half the height the live one had at the same font size. Matching
                            // the button's default is what makes the two the same shape.
                            lineHeight: 'normal',
                            ...elementStyleFrom(overlay.button.style),
                        }}
                    >
                        {overlay.button.label}
                    </a>
                )}
        </div>
    );

    // Narrowed by the null check rather than asserted: contentMaxWidth IS the full-bleed signal, and
    // the non-full-bleed branch is the one that doesn't need it.
    if (contentMaxWidth == null) {
        return <div style={overlayLayerStyleFrom(overlay.position)}>{content}</div>;
    }

    return (
        <div style={overlayFramedLayerStyle}>
            <div style={overlayScale
                ? overlayScaledFrameStyle(overlay.position)
                : overlayFrameStyleFrom(overlay.position, contentMaxWidth)}>
                {content}
            </div>
        </div>
    );
};

interface IStorefrontBannerEditorProps {
    // Full banner list (ALL pages), owned by the parent. Controlled so page-delete cleanup and the
    // batch-save full-list preservation happen in one place. Only the current page's banners are
    // DISPLAYED; new banners are stamped with `page`.
    banners: IStorefrontBanner[];
    onChange: (banners: IStorefrontBanner[]) => void;
    // Custom Page editing context (a page GUID). Undefined/empty = the main homepage.
    page?: string;
    // The storefront's custom pages — offered as "Move to Page" targets in the edit panel.
    pages?: IStorefrontPage[];
    height?: number;
    onHeightChange: (height?: number) => void;
    editable?: boolean;
    width?: number;
    ignoreGlobalBanners?: boolean;
    onIgnoreGlobalBannersChange?: (value: boolean) => void;
    // Auto full-bleed fill driven by bannerFullWidth: 'cover' crops to fill instead of letterboxing.
    // Used as the fallback when no explicit imageFit is configured. Defaults to 'contain'.
    objectFit?: 'contain' | 'cover';
    // homePageConfig.bannerAutoHeight (full-bleed only): size the carousel to the image's natural
    // height instead of a fixed-height box, so object-fit never has to crop. The live store honors
    // this for ANY number of banners — it paints only the current slide, so nothing has to share a
    // box height. Without it the preview letterboxed or cropped a hero the storefront showed whole.
    autoHeight?: boolean;
    // homePageConfig.bannerOverlayScale (full-bleed only): size the banner overlay in container-query
    // units so it scales with the banner instead of locking to pixels. Makes the banner box a query
    // container — see overlayScaledFrameStyle.
    overlayScale?: boolean;
    // homePageConfig.bannerDotsBelow: where the carousel dots sit. This preview has always drawn
    // them in flow UNDER the banner, while the storefront overlays them on the hero — so the gap the
    // merchant saw here did not exist for the shopper. The placement now follows the flag on both
    // sides; absent/false reproduces the storefront's shipped overlay.
    dotsBelow?: boolean;
    // Explicit per-storefront override of how the slide image fills its box: 'fit' letterboxes via
    // object-fit:contain; 'fill' crops via object-fit:cover. Takes precedence over objectFit.
    imageFit?: 'fit' | 'fill';
    // When set (full-width banner), the overlay text is locked to this page maxWidth (px)
    // even though the banner spans the full preview width.
    contentMaxWidth?: number;
    // Hero banner presentation for the page being edited, surfaced in the edit panel (main
    // homepage → homePageConfig fields; custom page → its pagesConfig entry).
    heroOptions?: IHeroBannerOptions;
    onHeroOptionsChange?: (patch: Partial<IHeroBannerOptions>) => void;
    // homePageConfig.bannerRotationSeconds — store-wide dwell seconds per slide. Drives this
    // preview's own cadence (WYSIWYG: unset = the live store's 7s, 0 = no auto-advance) and is
    // edited from the panel when onRotationSecondsChange is supplied (main homepage only —
    // the live store reads one value for every page's carousel).
    rotationSeconds?: number;
    onRotationSecondsChange?: (seconds?: number) => void;
    // One-shot open request from the Home Page Style bar's Banners… button (house pattern: the
    // request prop flips true, the effect below acts once and reports handled).
    openEditPanelRequest?: boolean;
    onOpenEditPanelHandled?: () => void;
    // False = another Home Page Style bar entry owns the right-hand surface now, so this panel gets
    // out of the way. These panels are non-blocking and anchored to the same edge, so two open at
    // once overlap. Undefined = no host is managing exclusion (the global product settings page
    // renders this editor on its own), and nothing is forced shut.
    panelActive?: boolean;
    // Reports the edit panel's open state so the bar button can show the active highlight — and
    // so an open that started from the CANVAS (clicking a banner) still claims the bar slot.
    onEditPanelOpenChange?: (open: boolean) => void;
}

const StorefrontBannerEditor: React.FC<IStorefrontBannerEditorProps> = (props) => {
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [isPaused, setIsPaused] = React.useState(false);
    // A link can open this panel: ?panel=banners. Kept as a trigger rather than deriving the state,
    // because editPanelOpen also pauses the carousel and scrolls to the selected slide.
    const bannerPanelLink = useUrlPanelState('banners');
    /*
     * Seeded from the URL rather than starting `false` and letting the effect below correct it.
     * A deep link lands with `?panel=banners` already set, so `bannerPanelLink.isOpen` is `true` on
     * the very first render — but effects fire in the order they're declared, and "mirror the panel's
     * open state back up" (below, keyed on `editPanelOpen`) runs in that SAME mount flush. It doesn't
     * see the `setEditPanelOpen(true)` this effect schedules — state updates from an earlier effect in
     * the same flush aren't visible to a later one — so it still reads the stale initial `false`,
     * disagrees with the URL, and closes the panel out from under this effect. That flips `editPanelOpen`
     * again next render, the mirror effect disagrees again the other way, and the two fight forever:
     * exactly the "Maximum update depth exceeded" loop this comment is here to prevent. Starting in
     * agreement with the URL removes the mismatch that kicks the fight off.
     */
    const [editPanelOpen, setEditPanelOpen] = React.useState(bannerPanelLink.isOpen);
    React.useEffect(() => {
        if (bannerPanelLink.isOpen) setEditPanelOpen(true);
    }, [bannerPanelLink.isOpen]);
    const location = useLocation();
    const [selectedBannerId, setSelectedBannerId] = React.useState<string | null>(null);
    /*
     * A per-slide deep link names its slide inside the setting id
     * (`storefront.banner.<bannerId>.<field>`), so select that slide rather than leaving the panel on
     * whatever was open. Without this the link opens the banner panel with no slide chosen and the
     * field it is pointing at is not rendered at all.
     */
    const focusedBannerId = React.useMemo(() => {
        const focus = new URLSearchParams(location.search).get('focus');
        return /^storefront\.banner\.([^.]+)\./.exec(focus ?? '')?.[1];
    }, [location.search]);
    React.useEffect(() => {
        if (focusedBannerId && props.banners.some((b) => b.id === focusedBannerId)) {
            setSelectedBannerId(focusedBannerId);
        }
    }, [focusedBannerId, props.banners]);

    const banners = props.banners;
    // Only this page's banners are shown in the carousel; the panel keeps/edits the full list.
    const pageBanners = React.useMemo(() => bannersForPage(banners, props.page), [banners, props.page]);
    const activeBanners = React.useMemo(
        () => pageBanners.filter((banner) => banner.disabled !== true),
        [pageBanners]
    );

    React.useEffect(() => {
        if (activeIndex >= activeBanners.length) {
            setActiveIndex(0);
        }
    }, [activeBanners.length, activeIndex]);

    // The shopper-side dwell time (this preview used to run its own 5s; matching the live pace is
    // what lets a merchant judge a configured interval here). null = auto-advance configured off.
    const rotateMs = bannerRotationMs({ bannerRotationSeconds: props.rotationSeconds });
    React.useEffect(() => {
        // Pause auto-rotation while the edit panel is open so the slide behind it stays put
        // (and instead follows the panel's selection — see the effect below).
        if (activeBanners.length < 2 || isPaused || editPanelOpen || rotateMs === null) {
            return;
        }

        const timer = window.setInterval(() => {
            setActiveIndex((current) => (current + 1) % activeBanners.length);
        }, rotateMs);

        return () => window.clearInterval(timer);
    }, [activeBanners.length, isPaused, editPanelOpen, rotateMs]);

    // While the panel is open, the carousel follows the panel's selected banner (WYSIWYG).
    // If the selected banner is disabled it won't be in activeBanners — keep the current
    // slide rather than jumping, and never index past the end.
    React.useEffect(() => {
        if (!editPanelOpen || !selectedBannerId) {
            return;
        }
        const index = activeBanners.findIndex((banner) => banner.id === selectedBannerId);
        if (index >= 0) {
            setActiveIndex(index);
        }
    }, [editPanelOpen, selectedBannerId, activeBanners]);

    // Declared above the early return below so the open-request effect can call it: the effect is a
    // hook and has to run on every render, and this is the one place the "which slide?" rule lives.
    const edit = () => {
        if (props.editable) {
            // Open with the currently-shown slide selected so editing starts on what's visible.
            setSelectedBannerId(activeBanners[activeIndex]?.id ?? pageBanners[0]?.id ?? null);
            setEditPanelOpen(true);
        }
    }

    // The Home Page Style bar's Banners… button. It can't just set editPanelOpen itself: the panel's
    // open state and the "select the visible slide first" rule both live here. The request is
    // reported handled even when the editor isn't editable, so a stuck flag can never swallow the
    // next click once edit mode is back on.
    React.useEffect(() => {
        if (!props.openEditPanelRequest) return;
        edit();
        props.onOpenEditPanelHandled?.();
    }, [props.openEditPanelRequest]);

    // Mirror the panel's open state back up (the bar button highlights while it's open).
    React.useEffect(() => {
        props.onEditPanelOpenChange?.(editPanelOpen);
    }, [editPanelOpen]);

    // Yield the surface when another bar entry takes it.
    React.useEffect(() => {
        if (props.panelActive === false) setEditPanelOpen(false);
    }, [props.panelActive]);

    if (!props.editable && activeBanners.length === 0) {
        return null;
    }

    // Explicit imageFit config wins; otherwise fall back to the bannerFullWidth-driven objectFit
    // ('cover' full-bleed, else 'contain'). Mirrors the shopper-side rule.
    const resolvedObjectFit: React.CSSProperties['objectFit'] =
        props.imageFit ? (props.imageFit === 'fill' ? 'cover' : 'contain') : (props.objectFit ?? 'contain');
    // Let the image's natural aspect ratio define the box (no clamp), matching the live storefront.
    // Two ways in:
    //  - autoHeight (homePageConfig.bannerAutoHeight): an explicit merchant choice, so it holds
    //    however many banners there are and whatever bannerHeight is set — same as the storefront.
    //  - a lone banner with no configured height: the long-standing implicit case.
    // Both require an image on the slide being shown: the shopper side drops image-less banners
    // entirely, and the editor's name placeholder needs the fixed-height box to sit in.
    const activeBanner = activeBanners[activeIndex];
    const naturalRatio = !!activeBanner?.imageUrl?.trim()
        && (!!props.autoHeight || (activeBanners.length === 1 && props.height == null));
    // Natural-ratio mode puts the slide in normal flow so the img (height:auto) can size the
    // container — an absolutely-positioned stack would collapse it to nothing. That means painting
    // ONE slide, which is exactly what the live carousel does (it renders `currentBanner` only);
    // the crossfade is a preview-only flourish and it is the thing that has to give here.
    const renderedBanners = naturalRatio ? activeBanners.slice(activeIndex, activeIndex + 1) : activeBanners;

    // The dot row, built once and placed by dotsBelow: over the hero (as the storefront ships
    // it) or in flow underneath. Built here so both placements render the identical control.
    const bannerDotsNode = activeBanners.length > 1 ? (
        <div
            style={props.dotsBelow ? {
                display: 'flex',
                gap: 8,
                justifyContent: 'center',
                alignItems: 'center',
                // Must match ac-storefront-web HomePage.tsx's below-banner dot row exactly — the
                // whole point of the flag is that the two placements agree.
                marginTop: 28,
                marginBottom: 14,
            } : {
                // Overlaid on the hero, as the storefront ships it. Absolute against the
                // banner box above, which is position:relative.
                position: 'absolute',
                bottom: 12,
                left: 0,
                right: 0,
                display: 'flex',
                gap: 8,
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            {activeBanners.map((banner, index) => (
                <button
                    key={banner.id || `${banner.name || 'dot'}-${index}`}
                    type='button'
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Show banner ${index + 1}`}
                    style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        border: 'none',
                        background: index === activeIndex ? '#0f6cbd' : '#b8c6d3',
                        cursor: 'pointer',
                        padding: 0
                    }}
                />
            ))}
        </div>
    ) : null;

    return (
        <div
            className={`storefront-banner-container ${props.editable ? 'storefront-banner-editable' : ''}`}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            <div
                style={{
                    // Two modes, mirroring the shopper home page:
                    //  - naturalRatio (single banner, no bannerHeight): no fixed height; the image
                    //    (width:100%, height:auto) defines the box height at its natural aspect ratio.
                    //  - otherwise: a fixed-height box (bannerHeight, default 350) with the image
                    //    letterboxed/cropped via object-fit, so wide banners present whole.
                    position: 'relative',
                    width: '100%',
                    height: naturalRatio ? undefined : (props.height ?? 350),
                    overflow: 'hidden',
                    // contentMaxWidth is only set in full-width (full-bleed hero) mode, where the
                    // live store renders the banner edge-to-edge with square corners — mirror that.
                    borderRadius: props.contentMaxWidth ? 0 : 12,
                    backgroundColor: '#f3f5f7',
                    // Makes the banner a container-query root so a scaled overlay can size itself in
                    // cqw (1% of THIS box's width). Without it the cqw/em values silently fall back
                    // to the inherited font size and the overlay renders at roughly a third scale.
                    ...(props.overlayScale ? { containerType: 'inline-size' } as React.CSSProperties : {}),
                }}
                onClick={edit}
            >
                {activeBanners.length === 0 && (
                    <div className='storefront-no-banner'>
                        No active banners
                    </div>
                )}
                {renderedBanners.map((banner, i) => {
                    // In natural-ratio mode the list is the single active slide, so its real index in
                    // activeBanners has to be restored — dot state, alt text and the edit handlers
                    // below are all keyed off it.
                    const index = naturalRatio ? activeIndex : i;
                    const isActive = index === activeIndex;
                    // naturalRatio mode renders the slide in normal flow so the img (height:auto)
                    // sizes the container. Fixed-height mode keeps the absolutely-positioned
                    // crossfade stack untouched.
                    const slideStyle: React.CSSProperties = naturalRatio ? {
                        position: 'relative',
                        backgroundColor: banner.backgroundColor || '#f3f5f7'
                    } : {
                        position: 'absolute',
                        inset: 0,
                        opacity: isActive ? 1 : 0,
                        transform: isActive ? 'scale(1)' : 'scale(1.02)',
                        transition: 'opacity 500ms ease, transform 500ms ease',
                        pointerEvents: isActive ? 'auto' : 'none',
                        backgroundColor: banner.backgroundColor || '#f3f5f7'
                    };

    return (
                        <div key={banner.id || `${banner.name || 'banner'}-${index}`} style={slideStyle}>
                            {banner.imageUrl ? (
                                <img
                                    src={banner.imageUrl}
                                    alt={banner.altText || banner.name || `Banner ${index + 1}`}
                                    loading={index === 0 ? 'eager' : 'lazy'}
                                    // naturalRatio: the image defines the box height at its own aspect
                                    // ratio. Fixed-height: fill the box, fit per resolvedObjectFit.
                                    style={naturalRatio
                                        ? { width: '100%', height: 'auto', display: 'block' }
                                        : { width: '100%', height: '100%', objectFit: resolvedObjectFit, display: 'block' }}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 20,
                                        fontWeight: 600,
                                        color: '#1f2933',
                                        background: 'linear-gradient(110deg, rgba(255,255,255,0.65), rgba(255,255,255,0.15))'
                                    }}
                                >
                                    {banner.name || `Banner ${index + 1}`}
                                </div>
                            )}
                            <BannerOverlay banner={banner} contentMaxWidth={props.contentMaxWidth} overlayScale={props.overlayScale} />
                        </div>
                    );
                })}
                {/* Overlaid placement lives INSIDE the banner box, which is the position:relative
                    ancestor its `bottom` is measured against — and matches where the storefront
                    puts it. The below-banner placement is a sibling after the box instead. */}
                {!props.dotsBelow && bannerDotsNode}
            </div>

            {props.dotsBelow && bannerDotsNode}
            <StorefrontBannerEditorPanel
                isOpen={editPanelOpen}
                onDismiss={() => { setEditPanelOpen(false); bannerPanelLink.close(); }}
                banners={banners}
                onChange={props.onChange}
                page={props.page}
                pages={props.pages}
                height={props.height}
                onHeightChange={props.onHeightChange}
                width={props.width}
                ignoreGlobalBanners={props.ignoreGlobalBanners}
                onIgnoreGlobalBannersChange={props.onIgnoreGlobalBannersChange}
                selectedBannerId={selectedBannerId}
                onSelectBanner={setSelectedBannerId}
                heroOptions={props.heroOptions}
                onHeroOptionsChange={props.onHeroOptionsChange}
                rotationSeconds={props.rotationSeconds}
                onRotationSecondsChange={props.onRotationSecondsChange}
            />
        </div>
    );
}

export default StorefrontBannerEditor;